import { defineStore } from 'pinia';
import {
    abxReasonError,
    createAbxIdempotencyKey,
    normalizeAbxReason,
} from '../components/abx/abxCaseActions.js';
import { parseAbxEtag } from '../components/abx/abxPrescription.js';
import { isAbxMutationPermissionError } from '../components/abx/abxWorkspaceErrors.js';
import { ApiV2Error, createApiV2Client } from '../services/apiV2.js';

const ABX_PROGRAM_CODE = 'abx-stewardship';
const CASE_STATUSES = new Set(['IN_PROGRESS', 'COMPLETE', 'EXCLUDED']);
const CASE_SOURCES = new Set(['SURVEILLANCE', 'WORKBOOK_IMPORT', 'PCC_IMPORT']);
const DEFAULT_PAGE_SIZE = 25;
const MAX_DATABASE_OFFSET = 2_147_483_647;
let nextRequestToken = 0;
let abxConfig = {
    apiRoot: null,
    fetchImpl: undefined,
};

export function configureAbxApi(config = {}) {
    abxConfig = {
        ...abxConfig,
        ...Object.fromEntries(
            ['apiRoot', 'fetchImpl']
                .filter((field) => field in config)
                .map((field) => [field, config[field]])
        ),
    };
}

// Order-independent identity of a worklist route query (list and detail share it).
export function abxWorklistKey(query = {}) {
    return JSON.stringify(
        Object.keys(query)
            .filter((key) => key !== 'prescription')
            .sort()
            .map((key) => [key, String(query[key])])
    );
}

export function normalizeAbxCaseQuery(query = {}) {
    const requestedPage = Number(query.page);
    const requestedPageSize = Number(query.pageSize);
    const search = String(query.search || '')
        .trim()
        .slice(0, 191);
    const status = CASE_STATUSES.has(query.status) ? query.status : undefined;
    const sourceWarning =
        query.sourceWarning === true || query.sourceWarning === 'true'
            ? true
            : query.sourceWarning === false || query.sourceWarning === 'false'
            ? false
            : undefined;

    const pageSize =
        Number.isSafeInteger(requestedPageSize) &&
        requestedPageSize > 0 &&
        requestedPageSize <= 100
            ? requestedPageSize
            : DEFAULT_PAGE_SIZE;
    const maximumPage = Math.floor(MAX_DATABASE_OFFSET / pageSize) + 1;
    const page =
        Number.isSafeInteger(requestedPage) &&
        requestedPage > 0 &&
        requestedPage <= maximumPage
            ? requestedPage
            : 1;

    const residentHash = String(query.residentHash || '').trim();
    // Site calendar dates on the case's Added date; a reversed range is swapped.
    const calendarDate = (value) =>
        /^\d{4}-\d{2}-\d{2}$/.test(value) &&
        !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
            ? value
            : '';
    let addedFrom = calendarDate(query.addedFrom);
    let addedTo = calendarDate(query.addedTo);
    if (addedFrom && addedTo && addedFrom > addedTo) {
        [addedFrom, addedTo] = [addedTo, addedFrom];
    }

    return {
        page,
        pageSize,
        ...(status ? { status } : {}),
        ...(sourceWarning === undefined ? {} : { sourceWarning }),
        ...(query.reviewDue === true || query.reviewDue === 'true'
            ? { reviewDue: true }
            : {}),
        ...(query.endingToday === true || query.endingToday === 'true'
            ? { endingToday: true }
            : {}),
        ...(query.over7Days === true || query.over7Days === 'true'
            ? { over7Days: true }
            : {}),
        ...(query.sourceChange === true || query.sourceChange === 'true'
            ? { sourceChange: true }
            : {}),
        ...(CASE_SOURCES.has(query.source) ? { source: query.source } : {}),
        ...(residentHash ? { residentHash } : {}),
        ...(search ? { search } : {}),
        ...(addedFrom ? { addedFrom } : {}),
        ...(addedTo ? { addedTo } : {}),
    };
}

export function abxClient() {
    return createApiV2Client({
        apiRoot: abxConfig.apiRoot,
        fetchImpl: abxConfig.fetchImpl || globalThis.fetch,
    });
}

function casePath(siteId, abxCaseId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-cases/${encodeURIComponent(abxCaseId)}`;
}

function settingsPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-settings`;
}

function qualityTargetsPath(siteId, scope = '') {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-quality-targets${
        scope ? `/${scope}` : ''
    }`;
}

function caseHistoryPath(siteId, abxCaseId) {
    return `${casePath(siteId, abxCaseId)}/history`;
}

function prescriptionPath(siteId, prescriptionId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-prescriptions/${encodeURIComponent(
        prescriptionId
    )}`;
}

export const ABX_ANTIMICROBIAL_FAMILIES = [
    'ANTIBACTERIAL',
    'ANTIFUNGAL',
    'ANTIVIRAL',
    'ANTIPARASITIC',
];

function cataloguePath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-antibiotics`;
}

function importsPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-imports`;
}

function prescribersPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/prescribers`;
}

function companyAntibioticsPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-company-antibiotics`;
}

function monthlyInputsPath(siteId, yearMonth = '') {
    const base = `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-monthly-inputs`;
    return yearMonth ? `${base}/${encodeURIComponent(yearMonth)}` : base;
}

function readinessPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-dashboard-readiness`;
}

function metricsPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-metrics`;
}

function overviewPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-overview`;
}
function reportPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-report`;
}

function reportsPath(siteId, reportingMonth = '') {
    const base = `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-reports`;
    return reportingMonth
        ? `${base}/${encodeURIComponent(reportingMonth)}`
        : base;
}

function exportsPath(siteId) {
    return `/sites/${encodeURIComponent(
        siteId
    )}/programs/${ABX_PROGRAM_CODE}/abx-exports`;
}

function companyAntibioticPath(siteId, entryId, suffix = '') {
    return `${companyAntibioticsPath(siteId)}/${encodeURIComponent(entryId)}${
        suffix ? `/${suffix}` : ''
    }`;
}

function resetMonthlyState(store) {
    store.monthlyYear = null;
    store.monthlyInputs = [];
    store.monthlyInputsLoading = false;
    store.monthlyInputsError = null;
    store.monthlyInputsRequestToken = null;
    store.monthlyDetail = null;
    store.monthlyDetailYearMonth = null;
    store.monthlyVersion = null;
    store.monthlyDetailLoading = false;
    store.monthlyDetailRequestToken = null;
    store.monthlySaving = false;
    store.monthlySaveError = null;
    store.monthlySaveRequestToken = null;
    store.monthlyInputConflict = false;
    store.monthlyForbidden = false;
}

function resetReportingState(store) {
    store.readiness = null;
    store.readinessYear = null;
    store.readinessLoading = false;
    store.readinessError = null;
    store.readinessRequestToken = null;
    store.metrics = null;
    store.metricsLoading = false;
    store.metricsError = null;
    store.metricsRequestToken = null;
    store.reportLedger = null;
    store.reportLedgerYear = null;
    store.reportLedgerLoading = false;
    store.reportLedgerError = null;
    store.reportLedgerRequestToken = null;
    store.reportDetail = null;
    store.reportDetailMonth = null;
    store.reportDetailLoading = false;
    store.reportDetailError = null;
    store.reportDetailRequestToken = null;
    store.exporting = false;
    store.exportError = null;
    store.exportRequestToken = null;
}

function resetCompanyAntibioticState(store) {
    store.antibiotics = [];
    store.antibioticsPagination = null;
    store.antibioticsLoading = false;
    store.antibioticsError = null;
    store.antibioticsRequestToken = null;
    store.companyAntibioticDetail = null;
    store.companyAntibioticVersion = null;
    store.companyAntibioticConflict = false;
    store.companyAntibioticForbidden = false;
}

function resetDetailState(store) {
    store.caseDetail = null;
    store.caseDetailId = null;
    store.caseDetailLoading = false;
    store.caseDetailError = null;
    store.caseDetailRequestToken = null;
    store.caseMutation = null;
    store.caseMutationError = null;
    store.caseMutationRequestToken = null;
    store.caseConflict = false;
    store.caseMutationForbidden = false;
    store.caseHistoryId = null;
    store.caseHistory = [];
    store.caseHistoryLoading = false;
    store.caseHistoryError = null;
    store.caseHistoryRequestToken = null;
    resetPrescriptionState(store);
}

function resetPrescriptionState(store) {
    store.prescriptionCaseId = null;
    store.prescriptions = [];
    store.prescriptionsLoading = false;
    store.prescriptionsLoaded = false;
    store.prescriptionsError = null;
    store.prescriptionsRequestToken = null;
    store.prescriptionDetail = null;
    store.prescriptionDetailId = null;
    store.prescriptionDetailCaseId = null;
    store.prescriptionVersion = null;
    store.prescriptionLoading = false;
    store.prescriptionError = null;
    store.prescriptionRequestToken = null;
    store.prescriptionSaving = false;
    store.prescriptionSaveError = null;
    store.prescriptionSaveRequestToken = null;
    store.prescriptionConflict = false;
    store.prescriptionMutationForbidden = false;
    store.prescriptionReplay = false;
    store.prescribers = [];
    store.prescribersLoading = false;
    store.prescribersError = null;
    store.prescribersRequestToken = null;
    store.catalogueItems = [];
    store.catalogueAvailability = null;
    store.catalogueLoading = false;
    store.catalogueError = null;
    store.catalogueQuery = '';
    store.catalogueRequestToken = null;
    store.companyAntibioticSaving = false;
    store.companyAntibioticError = null;
    store.companyAntibioticRequestToken = null;
}

function resetResidentState(store) {
    store.residents = [];
    store.residentPagination = null;
    store.residentCounts = null;
    store.residentsLoaded = false;
    store.residentsFetching = false;
    store.residentsError = null;
    store.residentsRequestToken = null;
}

// Overview owns its own work list and results so it never moves the Cases or Reports state.
function resetOverviewState(store) {
    store.overviewToday = null;
    store.overviewTodayLoading = false;
    store.overviewTodayError = null;
    store.overviewTodayRequestToken = null;
    store.overviewResults = null;
    store.overviewResultsLoading = false;
    store.overviewResultsError = null;
    store.overviewResultsRequestToken = null;
}

function isCurrentRequest(store, generation, siteId, token, tokenField) {
    return (
        store.generation === generation &&
        store.siteId === siteId &&
        store[tokenField] === token
    );
}

export const useAbxStore = defineStore('abx', {
    state: () => ({
        siteId: null,
        companyId: null,
        cases: [],
        casesWorklistKey: null,
        pagination: null,
        loaded: false,
        fetching: false,
        error: null,
        generation: 0,
        requestToken: null,
        residents: [],
        residentPagination: null,
        residentCounts: null,
        residentsLoaded: false,
        residentsFetching: false,
        residentsError: null,
        residentsRequestToken: null,
        caseDetail: null,
        caseDetailId: null,
        caseDetailLoading: false,
        caseDetailError: null,
        caseDetailRequestToken: null,
        caseMutation: null,
        caseMutationError: null,
        caseMutationRequestToken: null,
        caseConflict: false,
        caseMutationForbidden: false,
        caseHistoryId: null,
        caseHistory: [],
        caseHistoryLoading: false,
        caseHistoryError: null,
        caseHistoryRequestToken: null,
        prescriptionCaseId: null,
        prescriptions: [],
        prescriptionsLoading: false,
        prescriptionsLoaded: false,
        prescriptionsError: null,
        prescriptionsRequestToken: null,
        prescriptionDetail: null,
        prescriptionDetailId: null,
        prescriptionDetailCaseId: null,
        prescriptionVersion: null,
        prescriptionLoading: false,
        prescriptionError: null,
        prescriptionRequestToken: null,
        prescriptionSaving: false,
        prescriptionSaveError: null,
        prescriptionSaveRequestToken: null,
        prescriptionConflict: false,
        prescriptionMutationForbidden: false,
        prescriptionReplay: false,
        prescribers: [],
        prescribersLoading: false,
        prescribersError: null,
        prescribersRequestToken: null,
        catalogueItems: [],
        catalogueAvailability: null,
        catalogueLoading: false,
        catalogueError: null,
        catalogueQuery: '',
        catalogueRequestToken: null,
        companyAntibioticSaving: false,
        companyAntibioticError: null,
        companyAntibioticRequestToken: null,
        monthlyYear: null,
        monthlyInputs: [],
        monthlyInputsLoading: false,
        monthlyInputsError: null,
        monthlyInputsRequestToken: null,
        monthlyDetail: null,
        monthlyDetailYearMonth: null,
        monthlyVersion: null,
        monthlyDetailLoading: false,
        monthlyDetailRequestToken: null,
        monthlySaving: false,
        monthlySaveError: null,
        monthlySaveRequestToken: null,
        monthlyInputConflict: false,
        monthlyForbidden: false,
        readiness: null,
        readinessYear: null,
        readinessLoading: false,
        readinessError: null,
        readinessRequestToken: null,
        metrics: null,
        metricsLoading: false,
        metricsError: null,
        metricsRequestToken: null,
        reportLedger: null,
        reportLedgerYear: null,
        reportLedgerLoading: false,
        reportLedgerError: null,
        reportLedgerRequestToken: null,
        reportDetail: null,
        reportDetailMonth: null,
        reportDetailLoading: false,
        reportDetailError: null,
        reportDetailRequestToken: null,
        exporting: false,
        exportError: null,
        exportRequestToken: null,
        antibiotics: [],
        antibioticsPagination: null,
        antibioticsLoading: false,
        antibioticsError: null,
        antibioticsRequestToken: null,
        companyAntibioticDetail: null,
        companyAntibioticVersion: null,
        companyAntibioticConflict: false,
        companyAntibioticForbidden: false,
        overviewToday: null,
        overviewTodayLoading: false,
        overviewTodayError: null,
        overviewTodayRequestToken: null,
        overviewResults: null,
        overviewResultsLoading: false,
        overviewResultsError: null,
        overviewResultsRequestToken: null,
    }),
    actions: {
        setSiteContext(siteId = null, companyId = undefined) {
            const normalizedSiteId =
                siteId === null || siteId === undefined || siteId === ''
                    ? null
                    : String(siteId);
            const normalizedCompanyId =
                companyId === undefined
                    ? this.companyId
                    : companyId === null || companyId === ''
                    ? null
                    : String(companyId);
            if (
                this.siteId === normalizedSiteId &&
                this.companyId === normalizedCompanyId
            )
                return;

            this.generation += 1;
            this.siteId = normalizedSiteId;
            this.companyId = normalizedCompanyId;
            this.cases = [];
            this.casesWorklistKey = null;
            this.pagination = null;
            this.loaded = false;
            this.fetching = false;
            this.error = null;
            this.requestToken = null;
            resetResidentState(this);
            resetMonthlyState(this);
            resetReportingState(this);
            resetCompanyAntibioticState(this);
            resetDetailState(this);
            resetOverviewState(this);
        },
        // Site case-numbering settings; the caller owns loading/error state and stale-site checks.
        loadSettings({ siteId }) {
            return abxClient().request(settingsPath(siteId));
        },
        saveSettings({ siteId, caseCode, version }) {
            return abxClient().request(settingsPath(siteId), {
                method: 'PUT',
                body: { caseCode },
                ifMatch: version,
            });
        },
        // Quality targets: company targets, site overrides and the effective list.
        // The caller owns loading/error state and stale-site checks, as with loadSettings.
        loadQualityTargets({ siteId }) {
            return abxClient().request(qualityTargetsPath(siteId));
        },
        // `scope` is 'company' (body `targets`) or 'site' (body `overrides`).
        // Saved targets change the quality results, so cached results are cleared.
        async saveQualityTargets({ siteId, scope, items, version }) {
            const generation = this.generation;
            const storeSiteId = this.siteId;
            const response = await abxClient().request(
                qualityTargetsPath(siteId, scope),
                {
                    method: 'PUT',
                    body:
                        scope === 'company'
                            ? { targets: items }
                            : { overrides: items },
                    ifMatch: version,
                }
            );
            // A late response must not clear the state of a site opened after the save started.
            if (this.generation === generation && this.siteId === storeSiteId) {
                resetOverviewState(this);
                resetReportingState(this);
            }
            return response;
        },
        // `worklistKey` identifies the worklist route that asked, so case detail can trust its pager.
        async listCases({ siteId, worklistKey = null, ...query } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                throw new TypeError(
                    'An authorized site must be selected to load ABX Cases.'
                );
            }

            const requestQuery = normalizeAbxCaseQuery(query);
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.requestToken = requestToken;
            this.fetching = true;
            this.error = null;

            try {
                const response = await abxClient().request(
                    `/sites/${encodeURIComponent(
                        requestedSiteId
                    )}/programs/${ABX_PROGRAM_CODE}/abx-cases`,
                    { query: requestQuery }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'requestToken'
                    )
                ) {
                    this.cases = Array.isArray(response?.items)
                        ? response.items
                        : [];
                    this.pagination = response?.pagination || null;
                    this.casesWorklistKey = worklistKey;
                    this.loaded = true;
                    this.error = null;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'requestToken'
                    )
                ) {
                    this.error = error;
                    if (!this.loaded) {
                        this.cases = [];
                        this.casesWorklistKey = null;
                        this.pagination = null;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'requestToken'
                    )
                ) {
                    this.fetching = false;
                    this.requestToken = null;
                }
            }
        },
        async listResidents({ siteId, ...query } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                throw new TypeError(
                    'An authorized site must be selected to load ABX residents.'
                );
            }

            // Residents are never scoped to one resident or the legacy warning filter.
            const {
                residentHash: _residentHash,
                sourceWarning: _sourceWarning,
                ...requestQuery
            } = normalizeAbxCaseQuery(query);
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.residentsRequestToken = requestToken;
            this.residentsFetching = true;
            this.residentsError = null;
            const isCurrent = () =>
                isCurrentRequest(
                    this,
                    generation,
                    requestedSiteId,
                    requestToken,
                    'residentsRequestToken'
                );

            try {
                const response = await abxClient().request(
                    `/sites/${encodeURIComponent(
                        requestedSiteId
                    )}/programs/${ABX_PROGRAM_CODE}/abx-residents`,
                    { query: requestQuery }
                );
                if (isCurrent()) {
                    this.residents = Array.isArray(response?.items)
                        ? response.items
                        : [];
                    this.residentPagination = response?.pagination || null;
                    this.residentCounts = response?.counts || null;
                    this.residentsLoaded = true;
                }
                return response;
            } catch (error) {
                if (isCurrent()) {
                    this.residentsError = error;
                    if (!this.residentsLoaded) {
                        this.residents = [];
                        this.residentPagination = null;
                        this.residentCounts = null;
                    }
                }
                throw error;
            } finally {
                if (isCurrent()) {
                    this.residentsFetching = false;
                    this.residentsRequestToken = null;
                }
            }
        },
        // One resident group's case page; the caller owns the rows, so the flat worklist is untouched.
        async listResidentCases({
            siteId,
            residentHash,
            signal,
            ...query
        } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId || !residentHash) {
                throw new TypeError(
                    'An authorized site and resident are required to load ABX Cases.'
                );
            }

            const { sourceWarning: _sourceWarning, ...requestQuery } =
                normalizeAbxCaseQuery({ ...query, residentHash });
            const response = await abxClient().request(
                `/sites/${encodeURIComponent(
                    this.siteId
                )}/programs/${ABX_PROGRAM_CODE}/abx-cases`,
                { query: requestQuery, signal }
            );
            return {
                items: Array.isArray(response?.items) ? response.items : [],
                pagination: response?.pagination || null,
            };
        },
        async loadCase({ siteId, abxCaseId } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                throw new TypeError(
                    'An authorized site must be selected to load an ABX case.'
                );
            }
            if (
                abxCaseId === null ||
                abxCaseId === undefined ||
                abxCaseId === ''
            ) {
                throw new TypeError('An ABX case ID is required.');
            }

            const requestedCaseId = String(abxCaseId);
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            const changedCase = this.caseDetailId !== requestedCaseId;
            if (changedCase) {
                this.caseDetail = null;
                this.caseMutationForbidden = false;
            }
            this.caseDetailId = requestedCaseId;
            this.caseDetailRequestToken = requestToken;
            this.caseDetailLoading = true;
            this.caseDetailError = null;

            try {
                const response = await abxClient().request(
                    casePath(requestedSiteId, requestedCaseId)
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseDetailRequestToken'
                    ) &&
                    this.caseDetailId === requestedCaseId
                ) {
                    this.caseDetail = response || null;
                    this.caseDetailError = null;
                    this.caseConflict = false;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseDetailRequestToken'
                    ) &&
                    this.caseDetailId === requestedCaseId
                ) {
                    this.caseDetailError = error;
                    if (!this.caseDetail) this.caseDetail = null;
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseDetailRequestToken'
                    )
                ) {
                    this.caseDetailLoading = false;
                    this.caseDetailRequestToken = null;
                }
            }
        },
        async loadCaseHistory({ siteId, abxCaseId } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId || !abxCaseId) {
                throw new TypeError(
                    'An authorized site and ABX case are required to load history.'
                );
            }

            const requestedSiteId = this.siteId;
            const requestedCaseId = String(abxCaseId);
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            if (this.caseHistoryId !== requestedCaseId) {
                this.caseHistory = [];
            }
            this.caseHistoryId = requestedCaseId;
            this.caseHistoryRequestToken = requestToken;
            this.caseHistoryLoading = true;
            this.caseHistoryError = null;

            try {
                const response = await abxClient().request(
                    caseHistoryPath(requestedSiteId, requestedCaseId)
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseHistoryRequestToken'
                    ) &&
                    this.caseHistoryId === requestedCaseId
                ) {
                    this.caseHistory = Array.isArray(response?.items)
                        ? response.items
                        : [];
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseHistoryRequestToken'
                    ) &&
                    this.caseHistoryId === requestedCaseId
                ) {
                    this.caseHistoryError = error;
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseHistoryRequestToken'
                    )
                ) {
                    this.caseHistoryLoading = false;
                    this.caseHistoryRequestToken = null;
                }
            }
        },
        async mutateCase({ action, abxCaseId, reason, idempotencyKey } = {}) {
            if (!['exclude', 'reopen', 'complete'].includes(action)) {
                throw new TypeError('A supported ABX case action is required.');
            }
            if (!this.siteId || !this.caseDetail) {
                throw new TypeError('Load the ABX case before changing it.');
            }
            if (String(abxCaseId) !== String(this.caseDetailId)) {
                throw new TypeError('The loaded ABX case does not match.');
            }
            const version = this.caseDetail.version;
            if (!Number.isInteger(version) || version < 1) {
                throw new TypeError(
                    'The ABX case version must be a positive integer.'
                );
            }

            let body;
            if (action === 'complete') {
                body = { allKnownAntibioticsCaptured: true };
            } else {
                const validationError = abxReasonError(reason);
                if (validationError) throw new TypeError(validationError);
                body = { reason: normalizeAbxReason(reason) };
            }

            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestedCaseId = String(abxCaseId);
            const requestToken = ++nextRequestToken;
            const suffix =
                action === 'exclude'
                    ? 'exclusion'
                    : action === 'reopen'
                    ? 'reopening'
                    : 'completion';
            this.caseMutationRequestToken = requestToken;
            this.caseMutation = action;
            this.caseMutationError = null;
            this.caseConflict = false;

            try {
                const response = await abxClient().request(
                    `${casePath(requestedSiteId, requestedCaseId)}/${suffix}`,
                    {
                        method: 'POST',
                        ...(body ? { body } : {}),
                        ifMatch: version,
                        idempotencyKey:
                            idempotencyKey || createAbxIdempotencyKey(action),
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseMutationRequestToken'
                    ) &&
                    this.caseDetailId === requestedCaseId
                ) {
                    this.caseDetail = response || null;
                    this.caseDetailError = null;
                    this.caseMutationError = null;
                    this.caseConflict = false;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseMutationRequestToken'
                    ) &&
                    this.caseDetailId === requestedCaseId
                ) {
                    this.caseMutationError = error;
                    this.caseConflict = error?.status === 409;
                    if (isAbxMutationPermissionError(error)) {
                        this.caseMutationForbidden = true;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'caseMutationRequestToken'
                    )
                ) {
                    this.caseMutation = null;
                    this.caseMutationRequestToken = null;
                }
            }
        },
        excludeCase({ abxCaseId, reason, idempotencyKey } = {}) {
            return this.mutateCase({
                action: 'exclude',
                abxCaseId,
                reason,
                idempotencyKey,
            });
        },
        reopenCase({ abxCaseId, reason, idempotencyKey } = {}) {
            return this.mutateCase({
                action: 'reopen',
                abxCaseId,
                reason,
                idempotencyKey,
            });
        },
        completeCase({ abxCaseId, idempotencyKey } = {}) {
            return this.mutateCase({
                action: 'complete',
                abxCaseId,
                idempotencyKey,
            });
        },
        async loadPrescriptions({ siteId, abxCaseId } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId || !abxCaseId) {
                throw new TypeError(
                    'An authorized site and ABX case are required to load prescriptions.'
                );
            }

            const requestedSiteId = this.siteId;
            const requestedCaseId = String(abxCaseId);
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            if (this.prescriptionCaseId !== requestedCaseId) {
                this.prescriptions = [];
                this.prescriptionsLoaded = false;
            }
            this.prescriptionCaseId = requestedCaseId;
            this.prescriptionsRequestToken = requestToken;
            this.prescriptionsLoading = true;
            this.prescriptionsError = null;

            try {
                const items = [];
                let page = 1;
                let totalPages = 1;
                do {
                    const response = await abxClient().request(
                        `${casePath(
                            requestedSiteId,
                            requestedCaseId
                        )}/prescriptions`,
                        { query: { page, pageSize: 100 } }
                    );
                    items.push(
                        ...(Array.isArray(response?.items)
                            ? response.items
                            : [])
                    );
                    totalPages = Math.max(
                        1,
                        Number(response?.pagination?.totalPages) || 1
                    );
                    page += 1;
                } while (page <= totalPages);

                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionsRequestToken'
                    ) &&
                    this.prescriptionCaseId === requestedCaseId
                ) {
                    this.prescriptions = items;
                    this.prescriptionsLoaded = true;
                    this.prescriptionsError = null;
                }
                return items;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionsRequestToken'
                    ) &&
                    this.prescriptionCaseId === requestedCaseId
                ) {
                    this.prescriptionsError = error;
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionsRequestToken'
                    )
                ) {
                    this.prescriptionsLoading = false;
                    this.prescriptionsRequestToken = null;
                }
            }
        },
        async loadPrescribers({ siteId, search = '' } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId) {
                throw new TypeError(
                    'An authorized site is required to load Team members.'
                );
            }
            const requestedSiteId = this.siteId;
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.prescribersRequestToken = requestToken;
            this.prescribersLoading = true;
            this.prescribersError = null;
            try {
                const items = [];
                let page = 1;
                let totalPages = 1;
                do {
                    const response = await abxClient().request(
                        prescribersPath(requestedSiteId),
                        {
                            query: {
                                page,
                                pageSize: 100,
                                ...(String(search).trim()
                                    ? { search: String(search).trim() }
                                    : {}),
                            },
                        }
                    );
                    items.push(...(response?.items || []));
                    totalPages = Math.max(
                        1,
                        Number(response?.pagination?.totalPages) || 1
                    );
                    page += 1;
                } while (page <= totalPages);
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescribersRequestToken'
                    )
                ) {
                    this.prescribers = items;
                    this.prescribersError = null;
                }
                return items;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescribersRequestToken'
                    )
                ) {
                    this.prescribersError = error;
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescribersRequestToken'
                    )
                ) {
                    this.prescribersLoading = false;
                    this.prescribersRequestToken = null;
                }
            }
        },
        async loadPrescription({ siteId, abxCaseId, prescriptionId } = {}) {
            this.setSiteContext(siteId);
            if (!this.siteId || !abxCaseId || !prescriptionId) {
                throw new TypeError(
                    'An authorized site, ABX case, and prescription are required.'
                );
            }

            const requestedSiteId = this.siteId;
            const requestedCaseId = String(abxCaseId);
            const requestedPrescriptionId = String(prescriptionId);
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            if (
                this.prescriptionDetailId !== requestedPrescriptionId ||
                this.prescriptionDetailCaseId !== requestedCaseId
            ) {
                this.prescriptionDetail = null;
                this.prescriptionVersion = null;
                this.prescriptionMutationForbidden = false;
            }
            this.prescriptionDetailId = requestedPrescriptionId;
            this.prescriptionDetailCaseId = requestedCaseId;
            this.prescriptionRequestToken = requestToken;
            this.prescriptionLoading = true;
            this.prescriptionError = null;

            try {
                const response = await abxClient().request(
                    prescriptionPath(requestedSiteId, requestedPrescriptionId),
                    { includeResponseMeta: true }
                );
                if (String(response?.data?.abxCaseId) !== requestedCaseId) {
                    throw new ApiV2Error({
                        status: 404,
                        code: 'NOT_FOUND',
                        message: 'Prescription was not found.',
                    });
                }
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionRequestToken'
                    ) &&
                    this.prescriptionDetailId === requestedPrescriptionId
                ) {
                    this.prescriptionDetail = response.data;
                    this.prescriptionDetailCaseId = requestedCaseId;
                    this.prescriptionVersion = parseAbxEtag(
                        response.etag,
                        response.data?.version
                    );
                    this.prescriptionError = null;
                    this.prescriptionConflict = false;
                }
                return response.data;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionRequestToken'
                    ) &&
                    this.prescriptionDetailId === requestedPrescriptionId
                ) {
                    this.prescriptionError = error;
                    if (!this.prescriptionDetail) {
                        this.prescriptionVersion = null;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionRequestToken'
                    )
                ) {
                    this.prescriptionLoading = false;
                    this.prescriptionRequestToken = null;
                }
            }
        },
        async createPrescription({
            abxCaseId,
            prescription,
            idempotencyKey,
        } = {}) {
            if (!this.siteId || !abxCaseId) {
                throw new TypeError(
                    'Load the ABX case before creating a prescription.'
                );
            }
            const requestedSiteId = this.siteId;
            const requestedCaseId = String(abxCaseId);
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.prescriptionSaveRequestToken = requestToken;
            this.prescriptionSaving = true;
            this.prescriptionSaveError = null;
            this.prescriptionConflict = false;
            this.prescriptionReplay = false;

            try {
                const response = await abxClient().request(
                    `${casePath(
                        requestedSiteId,
                        requestedCaseId
                    )}/prescriptions`,
                    {
                        method: 'POST',
                        body: prescription,
                        idempotencyKey,
                        includeResponseMeta: true,
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionCaseId = requestedCaseId;
                    this.prescriptionDetail = response.data;
                    this.prescriptionDetailId = String(response.data.id);
                    this.prescriptionDetailCaseId = requestedCaseId;
                    this.prescriptionVersion = parseAbxEtag(
                        response.etag,
                        response.data?.version
                    );
                    this.prescriptionReplay = response.idempotencyReplayed;
                    this.prescriptionSaveError = null;
                    this.prescriptions = [
                        response.data,
                        ...this.prescriptions.filter(
                            (item) =>
                                String(item.id) !== String(response.data.id)
                        ),
                    ];
                    this.prescriptionsLoaded = true;
                }
                return response.data;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionSaveError = error;
                    this.prescriptionConflict = error?.status === 409;
                    if (isAbxMutationPermissionError(error)) {
                        this.prescriptionMutationForbidden = true;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionSaving = false;
                    this.prescriptionSaveRequestToken = null;
                }
            }
        },
        async patchPrescription({ abxCaseId, prescriptionId, patch } = {}) {
            if (!this.siteId || !this.prescriptionDetail) {
                throw new TypeError('Load the prescription before saving it.');
            }
            if (
                String(this.prescriptionDetailId) !== String(prescriptionId) ||
                String(this.prescriptionDetailCaseId) !== String(abxCaseId) ||
                String(this.prescriptionDetail.abxCaseId) !== String(abxCaseId)
            ) {
                throw new TypeError('The loaded prescription does not match.');
            }
            if (this.prescriptionDetail.status !== 'DRAFT') {
                throw new TypeError('Only a draft prescription can be edited.');
            }
            if (!Object.keys(patch || {}).length) {
                throw new TypeError(
                    'At least one prescription field is required.'
                );
            }
            if (!Number.isInteger(this.prescriptionVersion)) {
                throw new TypeError(
                    'A current prescription version is required.'
                );
            }

            const requestedSiteId = this.siteId;
            const requestedPrescriptionId = String(prescriptionId);
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.prescriptionSaveRequestToken = requestToken;
            this.prescriptionSaving = true;
            this.prescriptionSaveError = null;
            this.prescriptionConflict = false;

            try {
                const response = await abxClient().request(
                    prescriptionPath(requestedSiteId, requestedPrescriptionId),
                    {
                        method: 'PATCH',
                        body: patch,
                        ifMatch: this.prescriptionVersion,
                        includeResponseMeta: true,
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    ) &&
                    this.prescriptionDetailId === requestedPrescriptionId
                ) {
                    this.prescriptionDetail = response.data;
                    this.prescriptionVersion = parseAbxEtag(
                        response.etag,
                        response.data?.version
                    );
                    this.prescriptionSaveError = null;
                    this.prescriptionConflict = false;
                    this.prescriptions = this.prescriptions.map((item) =>
                        String(item.id) === requestedPrescriptionId
                            ? response.data
                            : item
                    );
                }
                return response.data;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    ) &&
                    this.prescriptionDetailId === requestedPrescriptionId
                ) {
                    this.prescriptionSaveError = error;
                    this.prescriptionConflict = error?.status === 409;
                    if (isAbxMutationPermissionError(error)) {
                        this.prescriptionMutationForbidden = true;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionSaving = false;
                    this.prescriptionSaveRequestToken = null;
                }
            }
        },
        async mutatePrescription({
            action,
            abxCaseId,
            prescriptionId,
            body,
            idempotencyKey,
        } = {}) {
            const suffixes = {
                finalize: 'finalization',
                amend: 'amendment',
                abandon: 'abandonment',
                void: 'voiding',
            };
            if (!suffixes[action]) {
                throw new TypeError(
                    'A supported prescription action is required.'
                );
            }
            if (
                !this.siteId ||
                !this.prescriptionDetail ||
                String(this.prescriptionDetailId) !== String(prescriptionId) ||
                String(this.prescriptionDetailCaseId) !== String(abxCaseId)
            ) {
                throw new TypeError(
                    'Load the matching prescription before changing it.'
                );
            }
            if (!Number.isInteger(this.prescriptionVersion)) {
                throw new TypeError(
                    'A current prescription version is required.'
                );
            }

            const requestedSiteId = this.siteId;
            const requestedPrescriptionId = String(prescriptionId);
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.prescriptionSaveRequestToken = requestToken;
            this.prescriptionSaving = true;
            this.prescriptionSaveError = null;
            this.prescriptionConflict = false;
            try {
                const response = await abxClient().request(
                    `${prescriptionPath(
                        requestedSiteId,
                        requestedPrescriptionId
                    )}/${suffixes[action]}`,
                    {
                        method: 'POST',
                        body,
                        ifMatch: this.prescriptionVersion,
                        ...(['confirm', 'correct'].includes(action)
                            ? {}
                            : {
                                  idempotencyKey:
                                      idempotencyKey ||
                                      createAbxIdempotencyKey(
                                          `prescription-${action}`
                                      ),
                              }),
                        includeResponseMeta: true,
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionDetail = response.data;
                    this.prescriptionVersion = parseAbxEtag(
                        response.etag,
                        response.data?.version
                    );
                    this.prescriptions = this.prescriptions.map((item) =>
                        String(item.id) === requestedPrescriptionId
                            ? response.data
                            : item
                    );
                }
                return response.data;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionSaveError = error;
                    this.prescriptionConflict = error?.status === 409;
                    if (isAbxMutationPermissionError(error)) {
                        this.prescriptionMutationForbidden = true;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'prescriptionSaveRequestToken'
                    )
                ) {
                    this.prescriptionSaving = false;
                    this.prescriptionSaveRequestToken = null;
                }
            }
        },
        finalizePrescription({
            abxCaseId,
            prescriptionId,
            prescription,
            idempotencyKey,
        }) {
            return this.mutatePrescription({
                action: 'finalize',
                abxCaseId,
                prescriptionId,
                body: { prescription },
                idempotencyKey,
            });
        },
        amendPrescription({
            abxCaseId,
            prescriptionId,
            reason,
            prescription,
            snapshotCorrection,
            idempotencyKey,
        }) {
            const validationError = abxReasonError(reason);
            if (validationError) throw new TypeError(validationError);
            return this.mutatePrescription({
                action: 'amend',
                abxCaseId,
                prescriptionId,
                body: {
                    reason: normalizeAbxReason(reason),
                    prescription,
                    ...(snapshotCorrection ? { snapshotCorrection } : {}),
                },
                idempotencyKey,
            });
        },
        abandonPrescription({
            abxCaseId,
            prescriptionId,
            reason,
            idempotencyKey,
        }) {
            const validationError = abxReasonError(reason);
            if (validationError) throw new TypeError(validationError);
            return this.mutatePrescription({
                action: 'abandon',
                abxCaseId,
                prescriptionId,
                body: { reason: normalizeAbxReason(reason) },
                idempotencyKey,
            });
        },
        voidPrescription({
            abxCaseId,
            prescriptionId,
            reason,
            idempotencyKey,
        }) {
            const validationError = abxReasonError(reason);
            if (validationError) throw new TypeError(validationError);
            return this.mutatePrescription({
                action: 'void',
                abxCaseId,
                prescriptionId,
                body: { reason: normalizeAbxReason(reason) },
                idempotencyKey,
            });
        },
        async listMonthlyInputs({ reportingYear } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.monthlyInputsRequestToken = requestToken;
            this.monthlyInputsLoading = true;
            this.monthlyInputsError = null;
            try {
                const response = await abxClient().request(
                    monthlyInputsPath(requestedSiteId),
                    {
                        query: { reportingYear, page: 1, pageSize: 100 },
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlyInputsRequestToken'
                    )
                ) {
                    this.monthlyYear = reportingYear;
                    this.monthlyInputs = response.items || [];
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlyInputsRequestToken'
                    )
                )
                    this.monthlyInputsError = error;
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlyInputsRequestToken'
                    )
                ) {
                    this.monthlyInputsLoading = false;
                    this.monthlyInputsRequestToken = null;
                }
            }
        },
        /** Canonical period report; period is sent as a JSON query value per the v2 contract. */
        async loadOverview() {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            return abxClient().request(overviewPath(this.siteId));
        },
        async loadOverviewToday() {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.overviewTodayRequestToken = requestToken;
            this.overviewTodayLoading = true;
            this.overviewTodayError = null;
            this.overviewToday = null;
            const current = () =>
                isCurrentRequest(
                    this,
                    generation,
                    requestedSiteId,
                    requestToken,
                    'overviewTodayRequestToken'
                );
            try {
                const response = await abxClient().request(
                    `${overviewPath(requestedSiteId)}-today`
                );
                if (current()) this.overviewToday = response;
                return response;
            } catch (error) {
                if (current()) this.overviewTodayError = error;
                throw error;
            } finally {
                if (current()) {
                    this.overviewTodayLoading = false;
                    this.overviewTodayRequestToken = null;
                }
            }
        },
        async loadOverviewResults({ reportingMonth } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.overviewResultsRequestToken = requestToken;
            this.overviewResultsLoading = true;
            this.overviewResultsError = null;
            this.overviewResults = null;
            const current = () =>
                isCurrentRequest(
                    this,
                    generation,
                    requestedSiteId,
                    requestToken,
                    'overviewResultsRequestToken'
                );
            try {
                const response = await abxClient().request(
                    `${overviewPath(requestedSiteId)}-results`,
                    { query: { reportingMonth } }
                );
                if (current()) {
                    // A newer readiness read can reject a month while this request runs.
                    const month = this.readiness?.months?.find(
                        (item) => item.yearMonth === reportingMonth
                    );
                    if (!month || month.reportingReady === true)
                        this.overviewResults = response;
                }
                return response;
            } catch (error) {
                if (current()) {
                    this.overviewResultsError = error;
                    if (
                        error.code === 'ABX_DASHBOARD_NOT_READY' &&
                        error.details?.readiness
                    ) {
                        this.readiness = error.details.readiness;
                        this.readinessYear = this.readiness.reportingYear;
                    }
                }
                throw error;
            } finally {
                if (current()) {
                    this.overviewResultsLoading = false;
                    this.overviewResultsRequestToken = null;
                }
            }
        },
        async loadPeriodReport({
            period,
            rateBasis = 1000,
            preliminary = false,
        } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const query = { period: JSON.stringify(period), rateBasis };
            if (preliminary) query.preliminary = 'true';
            return abxClient().request(reportPath(this.siteId), { query });
        },
        // Owner/admin only; the view keeps the result locally, so no shared store state.
        async loadMonthlyHistory({ yearMonth } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            return abxClient().request(
                `${monthlyInputsPath(this.siteId, yearMonth)}/history`,
                { query: { page: 1, pageSize: 100 } }
            );
        },
        async loadMonthlyInput({ yearMonth } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.monthlyDetailRequestToken = requestToken;
            this.monthlyDetailLoading = true;
            this.monthlyInputsError = null;
            try {
                const response = await abxClient().request(
                    monthlyInputsPath(requestedSiteId, yearMonth),
                    { includeResponseMeta: true }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlyDetailRequestToken'
                    )
                ) {
                    this.monthlyDetail = response.data;
                    this.monthlyDetailYearMonth = yearMonth;
                    this.monthlyVersion = parseAbxEtag(
                        response.etag,
                        response.data?.version
                    );
                    this.monthlyInputConflict = false;
                }
                return response.data;
            } catch (error) {
                if (error?.status === 404) {
                    if (
                        isCurrentRequest(
                            this,
                            generation,
                            requestedSiteId,
                            requestToken,
                            'monthlyDetailRequestToken'
                        )
                    ) {
                        this.monthlyDetail = null;
                        this.monthlyDetailYearMonth = yearMonth;
                        this.monthlyVersion = null;
                        this.monthlyInputConflict = false;
                    }
                    return null;
                }
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlyDetailRequestToken'
                    )
                )
                    this.monthlyInputsError = error;
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlyDetailRequestToken'
                    )
                ) {
                    this.monthlyDetailLoading = false;
                    this.monthlyDetailRequestToken = null;
                }
            }
        },
        async upsertMonthlyInput({
            yearMonth,
            fields,
            reason = '',
            idempotencyKey,
        } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            if (!fields || !Object.keys(fields).length)
                throw new TypeError('Change at least one monthly field.');
            const exists =
                this.monthlyDetailYearMonth === yearMonth &&
                Boolean(this.monthlyDetail);
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.monthlySaveRequestToken = requestToken;
            this.monthlySaving = true;
            this.monthlySaveError = null;
            this.monthlyInputConflict = false;
            try {
                const response = await abxClient().request(
                    monthlyInputsPath(requestedSiteId),
                    {
                        method: 'PUT',
                        body: {
                            yearMonth,
                            fields,
                            ...(reason.trim() ? { reason: reason.trim() } : {}),
                        },
                        ...(exists ? { ifMatch: this.monthlyVersion } : {}),
                        idempotencyKey:
                            idempotencyKey ||
                            createAbxIdempotencyKey('monthly-upsert'),
                        includeResponseMeta: true,
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlySaveRequestToken'
                    )
                ) {
                    this.monthlyDetail = response.data;
                    this.monthlyVersion = parseAbxEtag(
                        response.etag,
                        response.data?.version
                    );
                    this.monthlyInputs = [
                        ...this.monthlyInputs.filter(
                            (item) => item.yearMonth !== yearMonth
                        ),
                        response.data,
                    ];
                }
                return response.data;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlySaveRequestToken'
                    )
                ) {
                    this.monthlySaveError = error;
                    this.monthlyInputConflict = error?.status === 409;
                    if (isAbxMutationPermissionError(error)) {
                        this.monthlyForbidden = true;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'monthlySaveRequestToken'
                    )
                ) {
                    this.monthlySaving = false;
                    this.monthlySaveRequestToken = null;
                }
            }
        },
        // Reads one reporting year without touching the shared readiness state.
        // Returns null when the site context changed while the request ran.
        async fetchReadinessYear({ reportingYear } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const response = await abxClient().request(
                readinessPath(requestedSiteId),
                { query: { reportingYear } }
            );
            return this.generation === generation &&
                this.siteId === requestedSiteId
                ? response
                : null;
        },
        async loadReadiness({ reportingYear, cutoffMonth } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.readinessRequestToken = requestToken;
            this.readinessLoading = true;
            this.readinessError = null;
            try {
                const response = await abxClient().request(
                    readinessPath(requestedSiteId),
                    {
                        query: {
                            reportingYear,
                            ...(cutoffMonth ? { cutoffMonth } : {}),
                        },
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'readinessRequestToken'
                    )
                ) {
                    this.readiness = response;
                    this.readinessYear = reportingYear;
                    const selected = response.months?.find(
                        (month) =>
                            month.yearMonth ===
                            this.overviewResults?.reportingMonth
                    );
                    if (selected && selected.reportingReady !== true)
                        this.overviewResults = null;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'readinessRequestToken'
                    )
                )
                    this.readinessError = error;
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'readinessRequestToken'
                    )
                ) {
                    this.readinessLoading = false;
                    this.readinessRequestToken = null;
                }
            }
        },
        clearMetrics() {
            this.metrics = null;
            this.metricsError = null;
            this.metricsLoading = false;
            this.metricsRequestToken = null;
        },
        async loadReportLedger({ reportingYear } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.reportLedgerRequestToken = requestToken;
            this.reportLedgerLoading = true;
            this.reportLedgerError = null;
            try {
                const response = await abxClient().request(
                    reportsPath(requestedSiteId),
                    { query: { reportingYear } }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'reportLedgerRequestToken'
                    )
                ) {
                    this.reportLedger = response;
                    this.reportLedgerYear = reportingYear;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'reportLedgerRequestToken'
                    )
                )
                    this.reportLedgerError = error;
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'reportLedgerRequestToken'
                    )
                ) {
                    this.reportLedgerLoading = false;
                    this.reportLedgerRequestToken = null;
                }
            }
        },
        clearReportDetail() {
            this.reportDetail = null;
            this.reportDetailMonth = null;
            this.reportDetailError = null;
            this.reportDetailLoading = false;
            this.reportDetailRequestToken = null;
        },
        async loadReportDetail({ reportingMonth } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(String(reportingMonth || ''))) {
                throw new TypeError('A reporting month is required.');
            }
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.reportDetailRequestToken = requestToken;
            this.reportDetailLoading = true;
            this.reportDetailError = null;
            try {
                const response = await abxClient().request(
                    reportsPath(requestedSiteId, reportingMonth)
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'reportDetailRequestToken'
                    )
                ) {
                    this.reportDetail = response;
                    this.reportDetailMonth = reportingMonth;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'reportDetailRequestToken'
                    )
                ) {
                    this.reportDetail = null;
                    this.reportDetailMonth = null;
                    this.reportDetailError = error;
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'reportDetailRequestToken'
                    )
                ) {
                    this.reportDetailLoading = false;
                    this.reportDetailRequestToken = null;
                }
            }
        },
        async loadMetrics({ reportingYear, cutoffMonth, reportingMonth } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const selectedMonthReady = reportingMonth
                ? this.readiness?.months?.some(
                      (month) =>
                          month.yearMonth === reportingMonth &&
                          month.reportingReady
                  )
                : this.readiness?.ready;
            if (!selectedMonthReady) {
                throw new TypeError(
                    'Official metrics are unavailable until reporting is ready.'
                );
            }
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.metricsRequestToken = requestToken;
            this.metricsLoading = true;
            this.metricsError = null;
            try {
                const response = await abxClient().request(
                    metricsPath(requestedSiteId),
                    {
                        query: {
                            reportingYear,
                            ...(cutoffMonth ? { cutoffMonth } : {}),
                            ...(reportingMonth ? { reportingMonth } : {}),
                        },
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'metricsRequestToken'
                    )
                )
                    this.metrics = response;
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'metricsRequestToken'
                    )
                ) {
                    if (
                        error?.status === 409 &&
                        error?.code === 'ABX_DASHBOARD_NOT_READY' &&
                        error?.details?.readiness
                    ) {
                        this.readiness = error.details.readiness;
                        this.metrics = null;
                        this.metricsError = null;
                    } else {
                        this.metricsError = error;
                    }
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'metricsRequestToken'
                    )
                ) {
                    this.metricsLoading = false;
                    this.metricsRequestToken = null;
                }
            }
        },
        // Import review state lives in the dialog; these only bind requests to the current site.
        async previewImport(request) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            return abxClient().request(`${importsPath(this.siteId)}/preview`, {
                method: 'POST',
                body: request,
            });
        },
        // Active residents at the site, for linking imported people. Pages through all of them.
        async listSiteResidents() {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const path = `/sites/${encodeURIComponent(this.siteId)}/residents`;
            const items = [];
            // Moved-out and deceased residents too, so an import can link their old courses.
            for (const status of ['ACTIVE', 'LEFT_SITE', 'DECEASED']) {
                let page = 1;
                let totalPages = 1;
                do {
                    const result = await abxClient().request(path, {
                        query: { page, pageSize: 100, status },
                    });
                    items.push(...(result.items || []));
                    totalPages = Number(result.pagination?.totalPages || 1);
                    page += 1;
                } while (page <= totalPages);
            }
            return items;
        },
        // Imported cases only; Surveillance-linked cases derive their infection.
        async updateCaseInfection(abxCaseId, version, infection) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestedCaseId = String(abxCaseId);
            const response = await abxClient().request(
                `${casePath(requestedSiteId, requestedCaseId)}/infection`,
                {
                    method: 'PUT',
                    body: { infection },
                    ifMatch: version,
                }
            );
            if (
                generation === this.generation &&
                requestedSiteId === this.siteId &&
                this.caseDetailId === requestedCaseId
            ) {
                this.caseDetail = response || null;
            }
            return response;
        },
        async commitImport(request, { idempotencyKey } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            return abxClient().request(importsPath(this.siteId), {
                method: 'POST',
                body: request,
                idempotencyKey,
            });
        },
        async exportCsv({ resource, filters = {} } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.exportRequestToken = requestToken;
            this.exporting = true;
            this.exportError = null;
            try {
                return await abxClient().request(exportsPath(requestedSiteId), {
                    method: 'POST',
                    body: { resource, filters },
                    responseType: 'blob',
                    includeResponseMeta: true,
                });
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'exportRequestToken'
                    )
                )
                    this.exportError = error;
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'exportRequestToken'
                    )
                ) {
                    this.exporting = false;
                    this.exportRequestToken = null;
                }
            }
        },
        async loadMonthlyOverview({ reportingYear } = {}) {
            return Promise.all([
                this.listMonthlyInputs({ reportingYear }),
                this.loadReadiness({ reportingYear }),
            ]);
        },
        saveMonthlyInput(payload = {}) {
            return this.upsertMonthlyInput(payload);
        },
        async searchAntibiotics({ query, includeRetired = false } = {}) {
            if (!this.siteId) {
                throw new TypeError('An authorized site is required.');
            }
            const normalizedQuery = String(query || '').trim();
            if (normalizedQuery.length < 2) {
                this.catalogueItems = [];
                this.catalogueQuery = normalizedQuery;
                this.catalogueError = null;
                return [];
            }

            const requestedSiteId = this.siteId;
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.catalogueRequestToken = requestToken;
            this.catalogueLoading = true;
            this.catalogueError = null;
            this.catalogueQuery = normalizedQuery;
            try {
                const response = await abxClient().request(
                    cataloguePath(requestedSiteId),
                    {
                        query: {
                            query: normalizedQuery,
                            page: 1,
                            pageSize: 100,
                            includeRetired,
                        },
                    }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'catalogueRequestToken'
                    ) &&
                    this.catalogueQuery === normalizedQuery
                ) {
                    this.catalogueItems = Array.isArray(response?.items)
                        ? response.items
                        : [];
                    this.catalogueAvailability = response?.catalogue || null;
                    this.catalogueError = null;
                }
                return response?.items || [];
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'catalogueRequestToken'
                    )
                ) {
                    this.catalogueError = error;
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'catalogueRequestToken'
                    )
                ) {
                    this.catalogueLoading = false;
                    this.catalogueRequestToken = null;
                }
            }
        },
        async listAntibiotics({
            query = '',
            page = 1,
            pageSize = 25,
            includeRetired = false,
            source = 'ALL',
            family = '',
        } = {}) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const normalizedPage = normalizeAbxCaseQuery({ page, pageSize });
            const requestQuery = {
                ...normalizedPage,
                query: String(query || '')
                    .trim()
                    .slice(0, 255),
                includeRetired: Boolean(includeRetired),
                source: ['ALL', 'GLOBAL', 'COMPANY'].includes(source)
                    ? source
                    : 'ALL',
                ...(ABX_ANTIMICROBIAL_FAMILIES.includes(family)
                    ? { family }
                    : {}),
            };
            const generation = this.generation;
            const requestedSiteId = this.siteId;
            const requestToken = ++nextRequestToken;
            this.antibioticsRequestToken = requestToken;
            this.antibioticsLoading = true;
            this.antibioticsError = null;
            try {
                const response = await abxClient().request(
                    cataloguePath(requestedSiteId),
                    { query: requestQuery }
                );
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'antibioticsRequestToken'
                    )
                ) {
                    this.antibiotics = response.items || [];
                    this.antibioticsPagination = response.pagination || null;
                    this.catalogueAvailability = response.catalogue || null;
                }
                return response;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'antibioticsRequestToken'
                    )
                )
                    this.antibioticsError = error;
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'antibioticsRequestToken'
                    )
                ) {
                    this.antibioticsLoading = false;
                    this.antibioticsRequestToken = null;
                }
            }
        },
        async loadCompanyAntibiotic(entryId) {
            if (!this.siteId)
                throw new TypeError('An authorized site is required.');
            const requestedSiteId = this.siteId;
            const generation = this.generation;
            const response = await abxClient().request(
                companyAntibioticPath(requestedSiteId, entryId),
                { includeResponseMeta: true }
            );
            if (
                this.generation !== generation ||
                this.siteId !== requestedSiteId
            )
                return null;
            this.companyAntibioticDetail = response.data;
            this.companyAntibioticVersion = parseAbxEtag(
                response.etag,
                response.data?.version
            );
            this.companyAntibioticConflict = false;
            return response.data;
        },
        async updateCompanyAntibiotic(entryId, input) {
            if (!Number.isInteger(this.companyAntibioticVersion))
                throw new TypeError('Load the current company entry first.');
            return this.mutateCompanyAntibiotic(
                companyAntibioticPath(this.siteId, entryId),
                'PATCH',
                input
            );
        },
        async retireCompanyAntibiotic(entryId, input) {
            if (!Number.isInteger(this.companyAntibioticVersion))
                throw new TypeError('Load the current company entry first.');
            return this.mutateCompanyAntibiotic(
                companyAntibioticPath(this.siteId, entryId, 'retirement'),
                'POST',
                input
            );
        },
        async mutateCompanyAntibiotic(path, method, body) {
            const requestedSiteId = this.siteId;
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.companyAntibioticRequestToken = requestToken;
            this.companyAntibioticSaving = true;
            this.companyAntibioticError = null;
            this.companyAntibioticConflict = false;
            try {
                const response = await abxClient().request(path, {
                    method,
                    body,
                    ifMatch: this.companyAntibioticVersion,
                    includeResponseMeta: true,
                });
                if (
                    !isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'companyAntibioticRequestToken'
                    )
                )
                    return null;
                this.companyAntibioticDetail = response.data;
                this.companyAntibioticVersion = parseAbxEtag(
                    response.etag,
                    response.data?.version
                );
                return response.data;
            } catch (error) {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'companyAntibioticRequestToken'
                    )
                ) {
                    this.companyAntibioticError = error;
                    this.companyAntibioticConflict = error?.status === 409;
                    this.companyAntibioticForbidden =
                        isAbxMutationPermissionError(error);
                }
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'companyAntibioticRequestToken'
                    )
                ) {
                    this.companyAntibioticSaving = false;
                    this.companyAntibioticRequestToken = null;
                }
            }
        },
        async createCompanyAntibiotic(input) {
            if (!this.siteId) {
                throw new TypeError('An authorized site is required.');
            }
            const requestedSiteId = this.siteId;
            const generation = this.generation;
            const requestToken = ++nextRequestToken;
            this.companyAntibioticRequestToken = requestToken;
            this.companyAntibioticSaving = true;
            this.companyAntibioticError = null;
            try {
                const response = await abxClient().request(
                    companyAntibioticsPath(requestedSiteId),
                    { method: 'POST', body: input }
                );
                if (
                    !isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'companyAntibioticRequestToken'
                    )
                ) {
                    return null;
                }
                return response;
            } catch (error) {
                if (
                    !isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'companyAntibioticRequestToken'
                    )
                ) {
                    return null;
                }
                this.companyAntibioticError = error;
                this.companyAntibioticForbidden =
                    isAbxMutationPermissionError(error);
                throw error;
            } finally {
                if (
                    isCurrentRequest(
                        this,
                        generation,
                        requestedSiteId,
                        requestToken,
                        'companyAntibioticRequestToken'
                    )
                ) {
                    this.companyAntibioticSaving = false;
                    this.companyAntibioticRequestToken = null;
                }
            }
        },
        clearCompanyAntibioticEditor() {
            this.companyAntibioticDetail = null;
            this.companyAntibioticVersion = null;
            this.companyAntibioticConflict = false;
            this.companyAntibioticError = null;
        },
        clearPrescriptionEditor() {
            this.prescriptionDetail = null;
            this.prescriptionDetailId = null;
            this.prescriptionDetailCaseId = null;
            this.prescriptionVersion = null;
            this.prescriptionLoading = false;
            this.prescriptionError = null;
            this.prescriptionRequestToken = null;
            this.prescriptionSaving = false;
            this.prescriptionSaveError = null;
            this.prescriptionSaveRequestToken = null;
            this.prescriptionConflict = false;
            this.prescriptionMutationForbidden = false;
            this.prescriptionReplay = false;
            this.catalogueItems = [];
            this.catalogueAvailability = null;
            this.catalogueLoading = false;
            this.catalogueError = null;
            this.catalogueQuery = '';
            this.catalogueRequestToken = null;
            this.companyAntibioticSaving = false;
            this.companyAntibioticError = null;
            this.companyAntibioticRequestToken = null;
        },
        clear() {
            this.generation += 1;
            this.siteId = null;
            this.companyId = null;
            this.cases = [];
            this.casesWorklistKey = null;
            this.pagination = null;
            this.loaded = false;
            this.fetching = false;
            this.error = null;
            this.requestToken = null;
            resetResidentState(this);
            resetMonthlyState(this);
            resetReportingState(this);
            resetCompanyAntibioticState(this);
            resetDetailState(this);
            resetOverviewState(this);
        },
    },
});
